


















































































import { Component, Vue } from 'vue-property-decorator';
import ActionTypes from '@/constants/action-types';
import MenuBar from '@/components/menu/MenuBar.vue';
import Cart from '@/components/cart/Cart.vue';
import SubMenuSection from '@/components/menu/SubMenuSection.vue';
import { MenuModel } from '@/models/MenuModel';
import { MerchantDetailsModel } from '@/models/MerchantDetailsModel';
import MenuSelect from '@/components/menu/MenuSelect.vue';
import MenuPopup from '@/components/popups/menu-popup/MenuPopup.vue';
import { MenuItemModel } from '@/models/MenuItemModel';
import { CartItemModel, CartModel } from '@/models/CartModel';
import { SelectedMenuItem, SelectedMenuItemFull } from '@/types';
import InfoPopup from '@/components/popups/info-popup/InfoPopup.vue';
import { createSelectedMenuItemFull, saveOrUpdateCartItem } from '@/utils/cart-utils';
import { CartItemRemoveRequestModel } from '@/models/CartRequestModel';
import CartOrderFooter from '@/components/cart/CartOrderFooter.vue';
import MenuSelectDesktop from '@/components/menu/menu-select/MenuSelectDesktop.vue';
import MenuSelectMobile from '@/components/menu/menu-select/MenuSelectMobile.vue';
import MobileCartButton from '@/components/menu/MobileCartButton.vue';
import CartPopup from '@/components/popups/cart-popup/CartPopup.vue';
import OrderAheadPickupPopup from '@/components/popups/OrderAheadPickupPopup.vue';
import { LocationStatus } from '@/enums';
import moment from 'moment';
import EmptyMenuPopup from '@/components/popups/EmptyMenuPopup.vue';
import { daysOfTheWeekShort } from '@/utils/date-utils';

const TOP_OFFSET = 192;

@Component({
  components: {
    EmptyMenuPopup,
    OrderAheadPickupPopup,
    CartPopup,
    MobileCartButton,
    MenuSelectMobile,
    MenuSelectDesktop,
    CartOrderFooter,
    InfoPopup,
    MenuPopup,
    MenuSelect,
    SubMenuSection,
    Cart,
    MenuBar,
  },
  methods: {
    saveOrUpdateCartItem,
  },
})
export default class MerchantDetails extends Vue {
  private isLoading = false;

  private activeCategoryId: string | null = null;

  private categoryIdPrefix = 'category-';

  private selectedMenuItem: SelectedMenuItem | SelectedMenuItemFull | null = null;

  private cartPopupOpen = false;

  private orderAheadPickupPopupOpen = false;

  async mounted(): Promise<void> {
    await this.loadMenu();
    if (!this.isMenuEmpty) {
      this.setPickupTime();
      window.addEventListener('scroll', this.scrollListener);
    }
  }

  private get formattedPickupDateTime(): string | undefined {
    return this.pickupDateTime?.clone().tz(this.merchant?.timezone).format('dddd @ hh:mm a');
  }

  private get pickupDateTime(): moment.Moment | null {
    return this.$store.state.cart.pickupDateTime;
  }

  private setPickupTime(): void {
    const {
      nextClose, orderAheadNumDays, timezone, operatingHours,
    } = this.merchant;
    const isOpen = this.merchant.state === LocationStatus.Open;
    const isClose = this.merchant.state === LocationStatus.Closed;
    const now = moment();

    if (isClose || (isOpen && moment(nextClose).diff(now, 'minutes') < 15)) { // shop is closed or open and going to close in 15 minutes
      for (let i = 1; i <= orderAheadNumDays; i += 1) { // check next days
        const date = now.clone().tz(timezone).startOf('day').add(i, 'days');

        const operatingHour = operatingHours.find((o) => daysOfTheWeekShort.indexOf(o.day) === date.day());

        if (operatingHour?.open) { // ahead to this time + 15 min
          const open = new Date(operatingHour.open);

          const pickupTime = date.add(open.getUTCHours() * 60 + open.getUTCMinutes() + 15, 'minutes');

          this.$store.commit(`cart/${ActionTypes.SetPickupDateTime}`, pickupTime.utc());
          this.orderAheadPickupPopupOpen = true;
          return;
        }
      }
    } else if (isOpen && moment(nextClose).diff(now, 'minutes') > 15) { // shop is open and not going to close in 15 minutes
      const pickupTime = now.seconds(0).milliseconds(0);
      pickupTime.isAsapTime = true;

      this.$store.commit(`cart/${ActionTypes.SetPickupDateTime}`, pickupTime);
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('scroll', this.scrollListener);
  }

  private get merchant(): MerchantDetailsModel {
    return this.$store.state.merchant.merchant;
  }

  private get isMenuEmpty(): boolean {
    return this.merchant?.menus?.length === 0;
  }

  private get cart(): CartModel | null {
    return this.$store.state.cart.cart;
  }

  private get infoPopupOpen(): boolean {
    return this.$store.state.merchant.infoPopupOpen;
  }

  private get sections(): MenuModel[] {
    return this.merchant?.menus.filter((e: MenuModel) => e.visible) || [];
  }

  private get cartItemsCount(): number {
    return this.cart?.cartItems?.length || 0;
  }

  private onMenuItemClick(menuItem: MenuItemModel): void {
    this.selectedMenuItem = { menuItem, merchantId: this.merchant.id };
  }

  private onEditCartItem(cartItem: CartItemModel, menuItem: MenuItemModel, merchantId: number) {
    this.selectedMenuItem = createSelectedMenuItemFull(cartItem, menuItem, merchantId);
  }

  private async onRemoveCartItem(item: CartItemModel): Promise<void> {
    // if it's the last item, just empty cart
    if (this.cart?.cartItems?.length === 1) {
      await this.$store.dispatch(`cart/${ActionTypes.EmptyCart}`);
    } else {
      const removeItem: CartItemRemoveRequestModel = { cartItemId: item.id };
      await this.$store.dispatch(`cart/${ActionTypes.RemoveCartItem}`, removeItem);
    }
  }

  /**
   * Listen for an active category in viewport.
   */
  private scrollListener(): void {
    if (!Object.keys(this.$refs).length) return;

    const hasActive = Object.entries(this.$refs).some(([key, element]) => {
      const el = (element as Element[])[0];
      if (el && el.getBoundingClientRect().top <= TOP_OFFSET && el.getBoundingClientRect().bottom > TOP_OFFSET) {
        if (this.activeCategoryId !== key) {
          this.activeCategoryId = key;
        }
        return true;
      }
      return false;
    });

    if (!hasActive && this.activeCategoryId !== null) {
      this.activeCategoryId = null;
    }
  }

  private async loadMenu(): Promise<void> {
    const { id } = this.$route.params;
    if (!(this.merchant && this.merchant.id === Number(id))) {
      this.isLoading = true;
      await this.$store.dispatch(`merchant/${ActionTypes.GetMerchantDetails}`, id);
      this.isLoading = false;
    }
  }

  private setInfoPopupOpen(isOpen: boolean): void {
    this.$store.commit(`merchant/${ActionTypes.SetInfoPopupOpen}`, isOpen);
  }
}

